import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    App,
    Avatar,
    Button,
    Card,
    Col,
    DatePicker,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Statistic,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    CrownOutlined,
    DeleteOutlined,
    EditOutlined,
    KeyOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_DOMAIN } from '../components/constants';
import './HRM.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const EMPTY_FORM = {
    username: '',
    name: '',
    password: '',
    email: '',
    telephone: '',
    location: '',
    country: '',
    admin: false,
    user_type: undefined,
    wati_token: '',
    targetRange: null,
    targetAmount: null,
};

const normaliseMoreDetails = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

const toDateRange = (value) => {
    if (!Array.isArray(value) || value.length !== 2) return null;
    const dates = value.map((date) => dayjs(date));
    return dates.every((date) => date.isValid()) ? dates : null;
};

const HRM = () => {
    const { message, modal } = App.useApp();
    const [form] = Form.useForm();
    const [users, setUsers] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');

    const loadUsers = useCallback(async (showSuccess = false) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_DOMAIN}/auth/get_all_users`);
            if (response.data?.success !== true || !Array.isArray(response.data?.data)) {
                throw new Error(response.data?.message || 'The server returned an invalid user list.');
            }
            setUsers(response.data.data);
            if (showSuccess) message.success('User list refreshed');
        } catch (error) {
            setUsers([]);
            message.error(error.response?.data?.message || error.message || 'Unable to load users');
        } finally {
            setLoading(false);
        }
    }, [message]);

    const loadUserTypes = useCallback(async () => {
        try {
            const response = await axios.post(`${API_DOMAIN}/usertypes/get_user_types`, { api_key: 'Autoteile' });
            if (response.data?.success && Array.isArray(response.data.data)) {
                setUserTypes(response.data.data);
            }
        } catch (error) {
            message.warning(error.response?.data?.message || 'User types could not be loaded');
        }
    }, [message]);

    useEffect(() => {
        loadUsers();
        loadUserTypes();
    }, [loadUsers, loadUserTypes]);

    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();
        return users.filter((user) => {
            const isActive = user.status !== false && user.status !== 0 && user.status !== '0';
            const matchesStatus = statusFilter === 'all'
                || (statusFilter === 'active' && isActive)
                || (statusFilter === 'inactive' && !isActive);
            const matchesSearch = !query || [
                user.name,
                user.username,
                user.email,
                user.telephone,
                user.location,
                user.country,
                user.user_type,
            ].some((value) => String(value || '').toLowerCase().includes(query));
            return matchesStatus && matchesSearch;
        });
    }, [search, statusFilter, users]);

    const activeCount = useMemo(
        () => users.filter((user) => user.status !== false && user.status !== 0 && user.status !== '0').length,
        [users],
    );
    const adminCount = useMemo(() => users.filter((user) => Boolean(user.admin)).length, [users]);

    const openCreate = () => {
        setEditingUser(null);
        form.setFieldsValue(EMPTY_FORM);
        setDrawerOpen(true);
    };

    const openEdit = (user) => {
        const details = normaliseMoreDetails(user.moredetails);
        setEditingUser(user);
        form.setFieldsValue({
            ...EMPTY_FORM,
            username: user.username || '',
            name: user.name || '',
            email: user.email || '',
            telephone: user.telephone || '',
            location: user.location || '',
            country: user.country || '',
            admin: Boolean(user.admin),
            user_type: user.user_type || undefined,
            wati_token: user.wati_token || '',
            targetRange: toDateRange(details.targetRange),
            targetAmount: details.targetAmount ?? null,
        });
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setEditingUser(null);
        form.resetFields();
    };

    const saveUser = async (values) => {
        setSaving(true);
        try {
            const targetRange = values.targetRange
                ? values.targetRange.map((date) => date.format('YYYY-MM-DD'))
                : [];
            const payload = {
                id: editingUser?.id || null,
                username: values.username.trim(),
                name: values.name.trim(),
                password: values.password,
                email: values.email.trim(),
                telephone: values.telephone.replace(/\D/g, ''),
                location: values.location?.trim() || '',
                country: values.country?.trim() || '',
                admin: Boolean(values.admin),
                user_type: values.user_type || '',
                wati_token: values.wati_token?.trim() || '',
                moredetails: {
                    targetRange,
                    targetAmount: values.targetAmount ?? 0,
                },
            };
            const response = await axios.post(`${API_DOMAIN}/auth/register`, payload);
            if (response.data?.success !== true) {
                throw new Error(response.data?.message || response.data?.error || 'Unable to save user');
            }
            message.success(editingUser ? 'User updated successfully' : 'User created successfully');
            closeDrawer();
            await loadUsers();
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Unable to save user');
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = (user) => {
        modal.confirm({
            title: `Delete ${user.name || user.username}?`,
            content: 'This permanently removes the user account and cannot be undone.',
            okText: 'Delete user',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            async onOk() {
                const response = await axios.post(`${API_DOMAIN}/auth/delete_user`, { id: user.id });
                if (response.data?.success !== true) {
                    throw new Error(response.data?.message || 'Unable to delete user');
                }
                setUsers(Array.isArray(response.data.data)
                    ? response.data.data
                    : (current) => current.filter((item) => item.id !== user.id));
                message.success('User deleted successfully');
            },
        });
    };

    const columns = [
        {
            title: 'Employee',
            key: 'employee',
            fixed: 'left',
            width: 230,
            render: (_, user) => (
                <Space size={12}>
                    <Avatar className="hrm-user-avatar" icon={<UserOutlined />}>
                        {(user.name || user.username || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="hrm-user-copy">
                        <Text strong>{user.name || 'Unnamed user'}</Text>
                        <Text type="secondary">@{user.username || 'unknown'}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Contact',
            key: 'contact',
            width: 220,
            render: (_, user) => (
                <div className="hrm-user-copy">
                    <Text>{user.email || 'No email'}</Text>
                    <Text type="secondary">{user.telephone || 'No mobile'}</Text>
                </div>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'user_type',
            width: 150,
            render: (value) => value ? <Tag color="blue">{value}</Tag> : <Text type="secondary">Not assigned</Text>,
        },
        {
            title: 'Location',
            key: 'location',
            width: 180,
            render: (_, user) => [user.location, user.country].filter(Boolean).join(', ') || <Text type="secondary">Not provided</Text>,
        },
        {
            title: 'Access',
            key: 'access',
            width: 130,
            render: (_, user) => user.admin
                ? <Tag icon={<CrownOutlined />} color="gold">Admin</Tag>
                : <Tag>Standard</Tag>,
        },
        {
            title: 'WATI',
            dataIndex: 'wati_token',
            width: 110,
            render: (token) => token
                ? <Tag color="green">Connected</Tag>
                : <Tag color="default">Not set</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (status) => (status !== false && status !== 0 && status !== '0')
                ? <Tag color="success">Active</Tag>
                : <Tag color="error">Inactive</Tag>,
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            width: 96,
            align: 'right',
            render: (_, user) => (
                <Space size={4}>
                    <Tooltip title="Edit user">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(user)} />
                    </Tooltip>
                    <Tooltip title="Delete user">
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => deleteUser(user)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="hrm-page">
            <section className="hrm-hero">
                <div>
                    <Text className="hrm-eyebrow">Human resources</Text>
                    <Title level={2}>People & access</Title>
                    <Text type="secondary">Manage employee accounts, roles, targets, and service access.</Text>
                </div>
                <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={() => loadUsers(true)} loading={loading}>Refresh</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add employee</Button>
                </Space>
            </section>

            <Row gutter={[16, 16]} className="hrm-stats">
                <Col xs={24} sm={8}>
                    <Card><Statistic title="Total employees" value={users.length} prefix={<TeamOutlined />} /></Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card><Statistic title="Active accounts" value={activeCount} valueStyle={{ color: '#15803d' }} /></Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card><Statistic title="Administrators" value={adminCount} prefix={<CrownOutlined />} /></Card>
                </Col>
            </Row>

            <Card className="hrm-directory" bordered={false}>
                <div className="hrm-toolbar">
                    <div>
                        <Title level={4}>Employee directory</Title>
                        <Text type="secondary">{filteredUsers.length} {filteredUsers.length === 1 ? 'result' : 'results'}</Text>
                    </div>
                    <Space wrap className="hrm-filters">
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Search people, role, or location"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'all', label: 'All accounts' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                        />
                    </Space>
                </div>
                <Table
                    rowKey={(user) => user.id ?? user.username}
                    columns={columns}
                    dataSource={filteredUsers}
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} employees` }}
                    scroll={{ x: 1326 }}
                    locale={{ emptyText: <Empty description="No employees match these filters" /> }}
                />
            </Card>

            <Drawer
                title={editingUser ? 'Edit employee' : 'Add employee'}
                width={Math.min(620, window.innerWidth)}
                open={drawerOpen}
                onClose={closeDrawer}
                destroyOnClose
                extra={<Button type="primary" loading={saving} onClick={() => form.submit()}>Save</Button>}
            >
                <Form form={form} layout="vertical" initialValues={EMPTY_FORM} onFinish={saveUser} requiredMark="optional">
                    <div className="hrm-form-section">
                        <Title level={5}>Account details</Title>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="name" label="Full name" rules={[{ required: true, whitespace: true, message: 'Enter the employee name' }]}>
                                    <Input prefix={<UserOutlined />} placeholder="Full name" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="username" label="Username" rules={[{ required: true, whitespace: true, message: 'Enter a username' }]}>
                                    <Input autoComplete="off" placeholder="Username" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
                                    <Input placeholder="name@company.com" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="telephone"
                                    label="Mobile number"
                                    normalize={(value) => value?.replace(/[^0-9]/g, '')}
                                    rules={[
                                        { required: true, message: 'Enter a mobile number' },
                                        { pattern: /^\d{10,15}$/, message: 'Use 10–15 digits, including country code' },
                                    ]}
                                >
                                    <Input inputMode="numeric" placeholder="971501234567" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="password"
                                    label={editingUser ? 'New password' : 'Password'}
                                    extra={editingUser ? 'The current backend requires a new password when updating a user.' : null}
                                    rules={[{ required: true, min: 6, message: 'Enter at least 6 characters' }]}
                                >
                                    <Input.Password prefix={<KeyOutlined />} autoComplete="new-password" placeholder="At least 6 characters" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="hrm-form-section">
                        <Title level={5}>Role & access</Title>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="user_type" label="User type">
                                    <Select
                                        allowClear
                                        placeholder="Select a user type"
                                        options={userTypes.map((type) => ({ value: type.user_type, label: type.user_type }))}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="admin" label="Administrator" valuePropName="checked">
                                    <Switch checkedChildren="Admin" unCheckedChildren="Standard" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="wati_token" label="WATI token">
                                    <Input.Password visibilityToggle placeholder="Optional service token" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="hrm-form-section">
                        <Title level={5}>Location & target</Title>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="location" label="Location">
                                    <Input placeholder="City or branch" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="country" label="Country">
                                    <Input placeholder="Country" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={16}>
                                <Form.Item name="targetRange" label="Target period">
                                    <RangePicker format="YYYY-MM-DD" className="hrm-full-width" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item name="targetAmount" label="Target amount">
                                    <InputNumber min={0} precision={2} className="hrm-full-width" placeholder="0.00" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </Drawer>
        </div>
    );
};

const HRMPage = () => (
    <App>
        <HRM />
    </App>
);

export default HRMPage;
